import { useState, useEffect, useCallback, useMemo } from 'react'
import { Calendar, dateFnsLocalizer } from 'react-big-calendar'
import { format, parse, startOfWeek, getDay, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays } from 'date-fns'
import { enUS } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import { HiChevronLeft, HiChevronRight, HiPlus, HiX, HiCalendar, HiExclamationCircle } from 'react-icons/hi'
import { tasksApi } from '../../api/client'
import CreateTaskModal from './CreateTaskModal'
import 'react-big-calendar/lib/css/react-big-calendar.css'

const locales = { 'en-US': enUS }

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
})

const PRIORITY_COLORS = {
  critical: { bg: '#FEE2E2', border: '#EF4444', text: '#991B1B', darkBg: '#7F1D1D', darkText: '#FCA5A5' },
  high: { bg: '#FFEDD5', border: '#F97316', text: '#9A3412', darkBg: '#7C2D12', darkText: '#FDBA74' },
  medium: { bg: '#FEF3C7', border: '#F59E0B', text: '#92400E', darkBg: '#78350F', darkText: '#FCD34D' },
  low: { bg: '#DCFCE7', border: '#22C55E', text: '#166534', darkBg: '#14532D', darkText: '#86EFAC' },
  default: { bg: '#E0F2FE', border: '#0EA5E9', text: '#0369A1', darkBg: '#0C4A6E', darkText: '#7DD3FC' },
}

export default function CalendarView({ board, onUpdate }) {
  const navigate = useNavigate()
  const [columns, setColumns] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [view, setView] = useState('month')
  const [showCreateTask, setShowCreateTask] = useState(null)
  const [selectedTask, setSelectedTask] = useState(null)

  const fetchBoardData = useCallback(async () => {
    if (!board?.id) return
    try {
      setLoading(true)
      const response = await tasksApi.getBoardTasks(board.id)
      const data = Array.isArray(response.data?.columns) ? response.data.columns : []
      setColumns(data.map(col => ({ ...col, tasks: Array.isArray(col.tasks) ? col.tasks : [] })))
    } catch (error) {
      console.error('Failed to fetch board data:', error)
    } finally {
      setLoading(false)
    }
  }, [board?.id])

  useEffect(() => {
    fetchBoardData()
  }, [fetchBoardData])

  // Convert tasks to calendar events — include ALL tasks.
  // Tasks with due_date get placed on that date.
  // Tasks without due_date get placed on their created_at date (grayed out).
  const { events, totalTasks, tasksWithDueDate } = useMemo(() => {
    const allTasks = columns.flatMap(col => 
      (col.tasks || []).map(task => ({ ...task, columnName: col.name, columnId: col.id }))
    )

    const mapped = allTasks
      .filter(task => task.due_date) // Only show tasks that have due dates on the calendar
      .map(task => {
        const date = new Date(task.due_date)
        return {
          id: task.id,
          title: task.title,
          start: date,
          end: date,
          allDay: true,
          resource: task,
        }
      })

    return {
      events: mapped,
      totalTasks: allTasks.length,
      tasksWithDueDate: mapped.length,
    }
  }, [columns])

  const handleSelectEvent = (event) => {
    setSelectedTask(event.resource)
  }

  const handleSelectSlot = ({ start }) => {
    setShowCreateTask(start)
  }

  const handleCreateTask = async (taskData) => {
    try {
      const firstColumn = columns[0]
      await tasksApi.create({
        ...taskData,
        project_id: board.project_id,
        board_id: board.id,
        column_id: firstColumn?.id,
        due_date: showCreateTask instanceof Date ? showCreateTask.toISOString() : null,
      })
      setShowCreateTask(null)
      fetchBoardData()
      onUpdate?.()
    } catch (error) {
      console.error('Failed to create task:', error)
    }
  }

  const handleNavigate = (action) => {
    setCurrentDate(prev => {
      if (view === 'month') {
        if (action === 'PREV') return subMonths(prev, 1)
        if (action === 'NEXT') return addMonths(prev, 1)
      } else if (view === 'week') {
        if (action === 'PREV') return subWeeks(prev, 1)
        if (action === 'NEXT') return addWeeks(prev, 1)
      } else if (view === 'day') {
        if (action === 'PREV') return subDays(prev, 1)
        if (action === 'NEXT') return addDays(prev, 1)
      }
      return new Date() // TODAY
    })
  }

  const getDateLabel = () => {
    if (view === 'month') return format(currentDate, 'MMMM yyyy')
    if (view === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 0 })
      return `${format(start, 'MMM d')} – ${format(addDays(start, 6), 'MMM d, yyyy')}`
    }
    return format(currentDate, 'EEEE, MMMM d, yyyy')
  }

  // Custom event styling
  const eventStyleGetter = (event) => {
    const priority = event.resource?.priority
    const colors = PRIORITY_COLORS[priority] || PRIORITY_COLORS.default
    const isOverdue = new Date(event.start) < new Date() && event.resource?.status !== 'done'
    const isDone = event.resource?.status === 'done'
    const isDark = document.documentElement.classList.contains('dark')

    return {
      style: {
        backgroundColor: isDone
          ? (isDark ? '#14532D' : '#F0FDF4')
          : isOverdue
            ? (isDark ? '#7F1D1D' : '#FEE2E2')
            : (isDark ? colors.darkBg : colors.bg),
        borderLeft: `3px solid ${isDone ? '#22C55E' : isOverdue ? '#EF4444' : colors.border}`,
        color: isDone
          ? (isDark ? '#86EFAC' : '#166534')
          : isOverdue
            ? (isDark ? '#FCA5A5' : '#991B1B')
            : (isDark ? colors.darkText : colors.text),
        borderRadius: '4px',
        padding: '2px 6px',
        fontSize: '12px',
        fontWeight: 500,
        opacity: isDone ? 0.7 : 1,
        textDecoration: isDone ? 'line-through' : 'none',
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full"></div>
      </div>
    )
  }

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 220px)', minHeight: '500px' }}>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleNavigate('TODAY')}
            className="px-3 py-1.5 text-sm font-medium text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700 rounded-lg transition-colors"
          >
            Today
          </button>
          <div className="flex items-center border border-surface-200 dark:border-surface-600 rounded-lg overflow-hidden">
            <button onClick={() => handleNavigate('PREV')} className="p-1.5 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors">
              <HiChevronLeft className="w-5 h-5 text-surface-600 dark:text-surface-400" />
            </button>
            <button onClick={() => handleNavigate('NEXT')} className="p-1.5 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors">
              <HiChevronRight className="w-5 h-5 text-surface-600 dark:text-surface-400" />
            </button>
          </div>
          <h2 className="text-lg font-semibold text-surface-800 dark:text-surface-200 ml-2">{getDateLabel()}</h2>
        </div>

        <div className="flex items-center gap-2">
          {/* Task count indicator */}
          {totalTasks > 0 && tasksWithDueDate < totalTasks && (
            <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2.5 py-1 rounded-full">
              {totalTasks - tasksWithDueDate} task{totalTasks - tasksWithDueDate !== 1 ? 's' : ''} without due date
            </span>
          )}

          <div className="flex border border-surface-200 dark:border-surface-600 rounded-lg overflow-hidden">
            {['month', 'week', 'day'].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
                  view === v ? 'bg-brand-500 text-white' : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-700'
                }`}
              >
                {v}
              </button>
            ))}
          </div>

          <button onClick={() => setShowCreateTask(new Date())} className="btn btn-primary ml-2">
            <HiPlus className="w-4 h-4 mr-2" /> Add Task
          </button>
        </div>
      </div>

      {/* Calendar Container — explicit height is critical for react-big-calendar */}
      <div className="flex-1 bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700 overflow-hidden" style={{ minHeight: '400px' }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          date={currentDate}
          view={view}
          onNavigate={setCurrentDate}
          onView={setView}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          selectable
          eventPropGetter={eventStyleGetter}
          toolbar={false}
          views={['month', 'week', 'day']}
          style={{ height: '100%' }}
          popup
          dayPropGetter={(date) => {
            const isToday = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
            const isDark = document.documentElement.classList.contains('dark')
            if (isToday) {
              return { style: { backgroundColor: isDark ? 'rgba(14, 165, 233, 0.1)' : '#F0F9FF' } }
            }
            return { style: {} }
          }}
          messages={{
            showMore: (count) => `+${count} more`,
          }}
        />
      </div>

      {/* Empty state overlay */}
      {events.length === 0 && !loading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ position: 'relative', marginTop: '-200px' }}>
          <div className="text-center pointer-events-auto bg-white/90 dark:bg-surface-800/90 rounded-xl p-6 shadow-sm border border-surface-200 dark:border-surface-700">
            <HiCalendar className="w-12 h-12 text-surface-300 dark:text-surface-500 mx-auto mb-3" />
            <p className="text-surface-600 dark:text-surface-300 font-medium mb-1">No tasks on the calendar</p>
            <p className="text-sm text-surface-400 mb-3">
              {totalTasks > 0 
                ? `${totalTasks} task${totalTasks !== 1 ? 's' : ''} exist but none have due dates set.`
                : 'Create tasks with due dates to see them here.'
              }
            </p>
            <button onClick={() => setShowCreateTask(new Date())} className="btn btn-primary btn-sm">
              <HiPlus className="w-4 h-4 mr-1" /> Add Task with Due Date
            </button>
          </div>
        </div>
      )}

      {/* Task popup */}
      {selectedTask && (
        <TaskPopup
          task={selectedTask}
          columns={columns}
          onClose={() => setSelectedTask(null)}
          onNavigate={() => navigate(`/tasks/${selectedTask.id}`)}
          onUpdate={fetchBoardData}
        />
      )}

      {/* Create task modal */}
      {showCreateTask && (
        <CreateTaskModal
          columnId={columns[0]?.id}
          onClose={() => setShowCreateTask(null)}
          onCreate={handleCreateTask}
          defaultDueDate={showCreateTask instanceof Date ? format(showCreateTask, 'yyyy-MM-dd') : null}
        />
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 px-1">
        <span className="text-xs text-surface-500">Priority:</span>
        {Object.entries(PRIORITY_COLORS).filter(([key]) => key !== 'default').map(([key, colors]) => (
          <div key={key} className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }} />
            <span className="text-xs text-surface-600 dark:text-surface-400 capitalize">{key}</span>
          </div>
        ))}
        <div className="flex items-center gap-1 ml-2">
          <div className="w-3 h-3 rounded bg-red-100 border border-red-400" />
          <span className="text-xs text-surface-600 dark:text-surface-400">Overdue</span>
        </div>
      </div>
    </div>
  )
}

function TaskPopup({ task, columns, onClose, onNavigate, onUpdate }) {
  const handleStatusChange = async (newColumnId) => {
    try {
      await tasksApi.move(task.id, newColumnId, 0)
      onUpdate()
    } catch (error) {
      console.error('Failed to move task:', error)
    }
  }

  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'done'

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white dark:bg-surface-800 rounded-xl shadow-xl border border-surface-200 dark:border-surface-700 w-96 max-w-[90vw]">
        <div className="flex items-center justify-between p-4 border-b border-surface-200 dark:border-surface-700">
          <h3 className="font-semibold text-surface-800 dark:text-surface-200 truncate pr-4">{task.title}</h3>
          <button onClick={onClose} className="p-1 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded transition-colors">
            <HiX className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 space-y-3">
          {task.description && <p className="text-sm text-surface-600 dark:text-surface-400 line-clamp-3">{task.description}</p>}
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-surface-500 dark:text-surface-400">Column</span>
            <select
              value={task.columnId}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="px-2 py-1 text-sm bg-surface-100 dark:bg-surface-700 dark:text-surface-200 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              {columns.map(col => <option key={col.id} value={col.id}>{col.name}</option>)}
            </select>
          </div>

          {task.due_date && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-surface-500">Due Date</span>
              <span className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-surface-700 dark:text-surface-300'}`}>
                {format(new Date(task.due_date), 'MMM d, yyyy')}
                {isOverdue && ' (Overdue)'}
              </span>
            </div>
          )}

          {task.priority && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-surface-500">Priority</span>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${
                task.priority === 'critical' ? 'bg-red-100 text-red-700' :
                task.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-green-100 text-green-700'
              }`}>
                {task.priority}
              </span>
            </div>
          )}

          {task.assignee && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-surface-500">Assignee</span>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-medium">
                  {task.assignee.display_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <span className="text-sm text-surface-700 dark:text-surface-300">{task.assignee.display_name}</span>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-surface-200 dark:border-surface-700">
          <button onClick={onNavigate} className="w-full btn btn-primary">Open Task Details</button>
        </div>
      </div>
    </>
  )
}
